import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import * as fromGraphQl from '../notification-settings-list/graphql';

/**
 * Eine Zeile der Notification-Einstellungen (#735).
 *
 * Reine Praesentation: haelt keinen Zustand und fuehrt keine Mutation aus. Sperren,
 * optimistisches Umschalten und Fehlerbehandlung liegen in der Listen-Komponente.
 *
 * Muster nach issue-row (Doppelform-Selector, OnPush, Signal-inputs, host-Klassen).
 * Bewusst NICHT standalone — siehe .github/copilot-instructions.md.
 */
@Component({
  selector: 'div[notification-type-row], app-notification-type-row',
  templateUrl: './notification-type-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block border-b border-gray first:border-t'
  }
})
export class NotificationTypeRowComponent {

  readonly type = input.required<fromGraphQl.NotificationTypeInfo>();
  readonly locked = input(false);
  readonly sendingTest = input(false);
  readonly canSendTest = input(false);

  readonly toggled = output<{ channel: fromGraphQl.NotificationChannelKey; enabled: boolean }>();
  readonly testRequested = output<void>();

  /**
   * Kanal-Reihenfolge in der Zeile ist FEST In-App, E-Mail, Push — nicht die Array-Reihenfolge
   * des Servers, sonst springen die Spalten zwischen den Zeilen.
   */
  private static readonly CHANNEL_ORDER: fromGraphQl.NotificationChannelKey[] = [
    fromGraphQl.NotificationChannelKey.IN_APP,
    fromGraphQl.NotificationChannelKey.EMAIL,
    fromGraphQl.NotificationChannelKey.PUSH
  ];

  private static readonly CHANNEL_LABELS: Record<fromGraphQl.NotificationChannelKey, string> = {
    [fromGraphQl.NotificationChannelKey.IN_APP]: 'core.user.profile.notifications.channels.inApp',
    [fromGraphQl.NotificationChannelKey.EMAIL]: 'core.user.profile.notifications.channels.email',
    [fromGraphQl.NotificationChannelKey.PUSH]: 'core.user.profile.notifications.channels.push'
  };

  /**
   * Feste Slots in der Reihenfolge In-App, E-Mail, Push. Es wird JEDER gelieferte Kanal
   * gerendert (Entscheidung 2a) — ein nicht editierbarer erscheint als angehakte, deaktivierte
   * Checkbox, nicht als Luecke. null heisst ausschliesslich: der Server liefert den Kanal fuer diesen Typ
   * nicht (HIDDEN, heute allein tour.task.changed/E-Mail). Der Slot bleibt dann leer, statt zu
   * verschwinden, damit die Zeile spaltentreu zur Kopfzeile bleibt.
   */
  protected readonly channelSlots = computed<(fromGraphQl.NotificationChannelInfo | null)[]>(() => {
    const channels = this.type()?.channels ?? [];
    return NotificationTypeRowComponent.CHANNEL_ORDER.map(
      (key) => channels.find((channel) => channel.channel === key) ?? null
    );
  });

  protected channelLabel(channel: fromGraphQl.NotificationChannelKey): string {
    return NotificationTypeRowComponent.CHANNEL_LABELS[channel];
  }

  /** Leerer Text schaltet den PrimeNG-Tooltip ab (Tooltip.show prueft den Label auf truthy). */
  protected forcedHint(channel: fromGraphQl.NotificationChannelInfo): string {
    return channel.editable ? '' : 'core.user.profile.notifications.channelForced';
  }

  protected onToggle(channel: fromGraphQl.NotificationChannelInfo, enabled: boolean): void {
    // Die deaktivierte Checkbox feuert im DOM nicht; der Guard haelt zusaetzlich einen Aufruf
    // aus dem Template ab. Der Server wiese einen Toggle auf editable: false als
    // Validierungsfehler zurueck (Client-Doku 6.4).
    if (!channel.editable) {
      return;
    }
    this.toggled.emit({ channel: channel.channel, enabled });
  }
}
